import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';
import TacoFaq from '../faqs/TacoFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

function TacoCatering() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('catering_taco');

  const featuredTrucks = { food_categories: 'taco' };

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `Taco ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/taco-catering'
        description={cms?.meta?.description || `Book taco ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties. Compare quotes from local taco ${vehicleType}s.`}
        image={cdnPhoto(`${citySlug}-taco-food-truck-catering`)}
        structuredData={{
          cateringService: {
            name: `Taco ${vehicle_type} Catering in ${city_name}`,
            description: `Book taco ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties.`,
            url: '/food-truck-catering/taco-catering',
            serviceType: 'Taco Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Taco Catering', item: '/food-truck-catering/taco-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-taco-food-truck-catering.webp`} alt='Taco food truck catering at an event' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Taco ${vehicleType} catering in ${city_name}`}</h1>
            <CmsRichText
              html={cms?.hero?.subheadline}
              fallback={
                <p>Street tacos, Baja fish tacos, or a build-your-own taco bar. Tell us about the event and compare quotes from taco {vehicleType}s that can do it.</p>
              }
            />
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get taco catering quotes'}</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='taco'
        image={`${CDN_BASE}/photos/${citySlug}-taco-food-truck-menu.webp`}
        optionsText='Street tacos, Baja fish tacos, veggie tacos, and build-your-own taco bars.'
      />

      <FeaturedTrucks title={`Taco food ${vehicleType}s`} options={featuredTrucks} />

      <WhyBookWithUs />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What to expect from a taco {vehicleType}</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-authentic-taco-food-truck.webp`} alt='Taco truck with fresh ingredients' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Styles and menus</h4>
            <p>
              Al pastor, carne asada, barbacoa, and fish tacos on one {vehicleType}; fusion and vegan tacos on another. Many make their tortillas and salsas in-house. Menus are on each {vehicleType}&#39;s public profile, so compare before you request quotes.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-taco-catering-event.webp`} alt='Taco catering setup at an outdoor event' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>How service works</h4>
            <p>
              Guests order at the window and the {vehicleType} cooks to order. For a <Link to='/food-truck-catering/corporate-catering'>company lunch</Link> or a <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, ask about a taco bar or a short set menu; both keep the line moving. For a <Link to='/food-truck-catering/community-events'>festival</Link> or a big <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, ask each {vehicleType} how many guests it can serve per hour before you decide on one or two.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-taco-food-truck-dietary-options.webp`} alt='Taco truck with dietary-friendly options' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Dietary options</h4>
            <p>
              Most taco {vehicleType}s cover vegetarian and vegan, and corn tortillas make gluten-free easy. Note what you need in your request so quotes reflect it.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title='What customers say' />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <TacoFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default TacoCatering;
